import { closeIcon } from "@/assets/icons/close.icon";
import { html, LitElement, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { InviteMembersRequest, Role, GetRolesParams } from "@/types";
import { inviteMembers, getRoles } from "@/service";
import { Auth } from "@saasbase-io/loginflow-websdk";

@customElement("sb-invite-members-form")
export class InviteMembersForm extends LitElement {
  @property({ type: String }) drawerId = "sb-member-invite";
  @property({ type: String }) organizationId = "";

  @state() private emails: string[] = [];
  @state() private roles: string[] = [];
  @state() private availableRoles: {
    value: string;
    label: string;
    description?: string;
    icon?: string;
    disabled?: boolean;
  }[] = [];
  @state() private expiryDays = "7";
  @state() private customMessage = "";
  @state() private errors: { emails?: string; roles?: string } = {};
  @state() private open = false;
  @state() private isLoading = false;
  @state() private isLoadingRoles = false;

  protected createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener("open-sidedrawer", this._handleOpenDrawer);
    document.addEventListener("close-sidedrawer", this._handleCloseDrawer);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener("open-sidedrawer", this._handleOpenDrawer);
    document.removeEventListener("close-sidedrawer", this._handleCloseDrawer);
  }

  private _handleOpenDrawer = (e: Event) => {
    const detail = (e as CustomEvent).detail;
    const targetId = detail?.id || detail?.drawer_id;
    if (targetId && targetId !== this.drawerId) return;
    this.open = true;

    // Load roles when drawer opens
    if (this.organizationId) {
      this.loadRoles();
    }
  };

  private _handleCloseDrawer = (e: Event) => {
    const detail = (e as CustomEvent).detail;
    const targetId = detail?.id || detail?.drawer_id;
    if (targetId && targetId !== this.drawerId) return;
    this.open = false;
  };

  private _handleDrawerClosed = () => {
    // Sync when drawer closes via overlay or other means
    this.open = false;
  };

  private async loadRoles() {
    try {
      this.isLoadingRoles = true;

      // Fetch roles for the organization
      const params: GetRolesParams = {
        resource_id: this.organizationId,
        resource_type: "organization", // You can adjust this based on your needs
      };

      const response = await getRoles(params);

      // Map API roles to badge selector options
      this.availableRoles = response.data.map((role: Role) => ({
        value: role.key || role.name, // Use key if available, fallback to name
        label: role.name,
        description: role.description || undefined,
        // icon: role.icon || undefined, // Add if your roles have icons
        disabled: false,
      }));

      // Optionally, you can add system roles as well
      // If you want to include system roles, you can make another API call
      // or modify the params above
    } catch (error) {
      console.error("Failed to load roles:", error);
    } finally {
      this.isLoadingRoles = false;
    }
  }

  private close() {
    this.open = false;
    window.dispatchEvent(
      new CustomEvent("close-sidedrawer", { detail: { id: this.drawerId } }),
    );
  }

  private onEmailsChange(emails: string[]) {
    this.emails = emails;
    this.errors = { ...this.errors, emails: undefined };
  }

  private onRolesChange(roles: string[]) {
    this.roles = roles;
    this.errors = { ...this.errors, roles: undefined };
  }

  private handleExpiryInput(e: Event) {
    this.expiryDays = (e.target as HTMLInputElement).value;
  }

  private handleMessageInput(e: Event) {
    this.customMessage = (e.target as HTMLTextAreaElement).value;
  }

  private async handleSend() {
    const err: typeof this.errors = {};

    if (this.emails.length === 0) {
      err.emails = "Please enter at least one email address.";
    }
    if (this.roles.length === 0) {
      err.roles = "Please select at least one role.";
    }

    if (Object.keys(err).length > 0) {
      this.errors = err;
      return;
    }

    if (!this.organizationId) {
      console.error("Organization ID is required");
      return;
    }

    const payload: InviteMembersRequest = {
      emails: this.emails,
      roles: this.roles,
      // TO DO: CHANGE THIS LATER
      // invited_by: Auth.getAuthenticatedUser()?.sub,

      invited_by: "usr_06fgveyxgdtff295nb0se579cc",

      message: this.customMessage || undefined,
    };

    this.isLoading = true;

    try {
      const response = await inviteMembers(this.organizationId, payload);
      console.log("Invite sent successfully:", response);

      this.emails = [];
      this.roles = [];
      this.expiryDays = "7";
      this.customMessage = "";
      this.errors = {};

      window.dispatchEvent(
        new CustomEvent("invite-members-success", {
          detail: { organizationId: this.organizationId, response },
        }),
      );
      this.close();
    } catch (error) {
      console.error("Failed to send invites:", error);
      this.errors = {
        ...this.errors,
        emails: "Failed to send invites. Please try again.",
      };
    } finally {
      this.isLoading = false;
    }
  }

  render() {
    // Always render the sidedrawer; control visibility via .open property
    return html`
      <sb-sidedrawer
        id=${this.drawerId}
        side="right"
        inplace
        .open=${this.open}
        @sidedrawer-closed=${this._handleDrawerClosed}
      >
        <div part="container" class="sb-flex sb-flex-col sb-h-full">
          <!-- Header -->
          <div>
            <div class="sb-flex sb-justify-between sb-items-center">
              <rtg-typography-h level="h4">Invite Members</rtg-typography-h>
              <rtg-button
                variant="ghost"
                size="icon-sm"
                part="modal-close-button"
                @click=${this.close}
                ?disabled=${this.isLoading}
              >
                ${closeIcon()}
              </rtg-button>
            </div>
            <rtg-typography-muted>
              Invite members by email or create a shareable invitation link
            </rtg-typography-muted>
          </div>

          <!-- Scrollable form -->
          <div
            class="sb-flex-1 sb-overflow-y-auto sb-flex sb-flex-col sb-gap-4 sb-mt-10 sb-pl-2"
          >
            <!-- Emails -->
            <div>
              <sb-multi-email-input
                label="Emails"
                .value=${this.emails}
                .onChange=${(e: string[]) => this.onEmailsChange(e)}
                placeholder="Type or paste emails"
                .maxEmails=${10}
                ?disabled=${this.isLoading}
              ></sb-multi-email-input>
              ${this.errors.emails
                ? html`<rtg-typography-small part="error"
                    >${this.errors.emails}</rtg-typography-small
                  >`
                : nothing}
            </div>

            <!-- Roles -->
            <div>
              ${this.isLoadingRoles
                ? html`
                    <div class="sb-flex sb-items-center sb-gap-2 sb-mt-2">
                      <rtg-spinner size="sm"></rtg-spinner>
                      <rtg-typography-muted
                        >Loading roles...</rtg-typography-muted
                      >
                    </div>
                  `
                : html`
                    <sb-badge-selector
                      mode="multi"
                      label="Roles"
                      .options=${this.availableRoles}
                      .value=${this.roles}
                      .onChange=${(roles: string[]) =>
                        this.onRolesChange(roles)}
                      showDescriptions
                      ariaLabel="Select roles"
                      ?disabled=${this.isLoading || this.isLoadingRoles}
                    ></sb-badge-selector>
                  `}
              <rtg-typography-muted class="sb-mt-1">
                Select one or more roles for invited members
              </rtg-typography-muted>
              ${this.errors.roles
                ? html`<rtg-typography-small part="error"
                    >${this.errors.roles}</rtg-typography-small
                  >`
                : nothing}
            </div>

            <!-- Expiry -->
            <div class="sb-space-y-2">
              <rtg-label>Invitation expiry</rtg-label>
              <rtg-input-group>
                <rtg-input-group-input
                  type="number"
                  .value=${this.expiryDays}
                  @input=${this.handleExpiryInput}
                  ?disabled=${this.isLoading}
                ></rtg-input-group-input>
                <rtg-input-group-addon align="inline-end">
                  <rtg-input-group-text>Days</rtg-input-group-text>
                </rtg-input-group-addon>
              </rtg-input-group>
              <rtg-typography-muted>
                The invitation will expire after this period
              </rtg-typography-muted>
            </div>

            <!-- Custom message -->
            <div class="sb-space-y-2">
              <rtg-label>
                Custom message
                <rtg-typography-muted>(Optional)</rtg-typography-muted>
              </rtg-label>
              <rtg-textarea
                placeholder="Type your message here."
                .value=${this.customMessage}
                @input=${this.handleMessageInput}
                class="rtg-max-w-xs"
                ?disabled=${this.isLoading}
              ></rtg-textarea>
            </div>
          </div>

          <!-- Footer buttons -->
          <div class="sb-flex sb-justify-end sb-gap-2">
            <rtg-button
              variant="ghost"
              @click=${this.close}
              ?disabled=${this.isLoading}
            >
              Cancel
            </rtg-button>
            <rtg-button
              variant="default"
              ?disabled=${this.emails.length === 0 ||
              this.roles.length === 0 ||
              this.isLoading ||
              this.isLoadingRoles}
              @click=${this.handleSend}
            >
              ${this.isLoading
                ? html`
                    <span class="sb-flex sb-items-center sb-gap-2">
                      <rtg-spinner size="sm"></rtg-spinner>
                      Sending...
                    </span>
                  `
                : html`
                    Send ${this.emails.length}
                    Email${this.emails.length !== 1 ? "s" : ""}
                  `}
            </rtg-button>
          </div>
        </div>
      </sb-sidedrawer>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "sb-invite-members-form": InviteMembersForm;
  }
}
